import type { GridOptions } from '../types'
import { isGridOption } from '../utils/is-grid-option'
export interface BaseControllerPageResult<T = unknown> {
  code: number
  success: boolean
  data: T
  total?: number
  msg?: string
}

export function parsePageResult<T = unknown>(
  body: BaseControllerPageResult<T[]>
): { list: T[]; total: number } {
  const list = (Array.isArray(body.data) ? body.data : []) as T[]
  return {
    list,
    total: body.total ?? list.length,
  }
}

export function resolveModuleUrls(module: string) {
  return {
    queryByExamplePage: `${module}/queryByExamplePage`,
    queryDistinctColumnValues: `${module}/queryDistinctColumnValues`,
    insertRow: `${module}/insertRow`,
    batchInsert: `${module}/batchInsert`,
    updateById: `${module}/updateById`,
    deleteById: `${module}/deleteById`,
    batchDelete: `${module}/batchDelete`,
    copy: `${module}/copy`,
  }
}

export function hasPersistedRowId(row: Record<string, unknown> | null | undefined) {
  return row != null && row.id != null && row.id !== ''
}

/** 移除表格运行时字段，避免提交到后端 */
export function stripGridInternalFields(data: Record<string, unknown>) {
  delete data._gridRowId
  return data
}

export function prepareRowForInsert(row: Record<string, unknown>) {
  const data = { ...row }
  stripGridInternalFields(data)
  if (!hasPersistedRowId(data)) {
    delete data.id
  }
  return data
}

export function prepareRowForUpdate(row: Record<string, unknown>) {
  const data = { ...row }
  return stripGridInternalFields(data)
}

/** 浅复制：克隆源行并合并 defaultRow，清除持久化标识 */
export function prepareRowForCopy(
  source: Record<string, unknown>,
  defaultRow?: Record<string, unknown>
) {
  const record = { ...source, ...(defaultRow || {}) }
  delete record.id
  stripGridInternalFields(record)
  return record
}

export function isNewGridRow(row: Record<string, unknown>) {
  return !hasPersistedRowId(row)
}

export function resolveHeadIdField(options: GridOptions) {
  if (options.headId) {
    return options.headId
  }
  return options.parent ? 'headId' : ''
}

function resolveParentRow(options: GridOptions) {
  return resolveParentCurrentRow(options.parent)
}

export function resolveParentCurrentRow(
  parent: GridOptions['parent']
): Record<string, unknown> | null {
  if (!parent || typeof parent !== 'object') {
    return null
  }
  if (isGridOption(parent)) {
    return parent.getCurrentRow?.() ?? null
  }
  if ('getCurrentRow' in parent && typeof parent.getCurrentRow === 'function') {
    return parent.getCurrentRow() ?? null
  }
  if ('currentRow' in parent) {
    return (parent as GridOptions).currentRow ?? null
  }
  return parent as Record<string, unknown>
}

export function resolveHeadIdValue(options: GridOptions): unknown {
  const field = resolveHeadIdField(options)
  if (!field) {
    return undefined
  }
  const paramValue = options.params?.[field]
  if (paramValue != null && paramValue !== '') {
    return paramValue
  }
  const parentRow = resolveParentRow(options)
  if (hasPersistedRowId(parentRow)) {
    return parentRow.id
  }
  return -1
}

export function appendHeadIdToRow(
  row: Record<string, unknown>,
  options: GridOptions
) {
  const field = resolveHeadIdField(options)
  if (field && row[field] == null) {
    row[field] = resolveHeadIdValue(options)
  }
  if (options.params) {
    const queryOnlyKeys = new Set(['currentPage', 'pageSize', 'pageFlag', 'sorts', 'filters'])
    for (const [key, value] of Object.entries(options.params)) {
      if (queryOnlyKeys.has(key) || value == null || row[key] != null) {
        continue
      }
      row[key] = value
    }
  }
  return row
}

export function resolveParentRowId(parent: GridOptions['parent']): unknown {
  const row = resolveParentCurrentRow(parent)
  return row?.id
}
